import React, {FC} from 'react';
import styled from 'styled-components';

import {KeyboardActionsEnum} from '../../../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {PaletteColorsEnum} from '../../../../../../libs/shared/core/src/models/api/paletteModel';
import {TeammateModel} from '../../../../../../libs/shared/core/src/models/api/teammateModel';
import {isTeamNamespace} from '../../../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {CellsContext} from '../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {Avatar} from '../../../../../../libs/shared/ui/src/components/avatar/avatar';
import {AvatarBackground} from '../../../../../../libs/shared/ui/src/components/avatar/avatarBackground';
import {TeammateAvatar} from '../../../../../../libs/shared/ui/src/components/avatar/teammateAvatar';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {
  ButtonVariantsEnum,
  findButtonColors,
} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ToolbarButton} from '../../../../../../libs/shared/ui/src/components/buttons/toolbarButton';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisuallyHidden} from '../../../../../../libs/shared/ui/src/components/visuallyHidden/visuallyHidden';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {UserTheme} from '../../../../../../libs/shared/ui/src/theme/themeTypes';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {VisualConversationTrackerModel} from '../../../../../../srcCommon/inboxes/viewer/conversation/participants/inviteParticipantsHelpers';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {
  computeShouldShowTrackers,
  computeSubscriberCount,
} from '../../../../../store/data/conversations/conversationsHelpers';
import {InviteDropdown} from './invite/inviteDropdown';

/*
 * Props.
 */

interface TrackerProps {
  teammate: TeammateModel;
  hasSeenAll: boolean;
  hasMuted: boolean;
}

interface ConversationTopbarInviteProps {
  user: TeammateModel;
  cellsContext: CellsContext;
  conversation: ConversationModel;
  trackers: ReadonlyArray<TrackerProps>;
  visibleTrackers: ReadonlyArray<TrackerProps>;
  otherTrackersCount: number;
}

/*
 * Style.
 */

const StyledInviteDiv = styled.div`
  display: flex;
  align-items: center;
  margin: -1px -5px -2px -4px;
`;

const StyledAvatarsDiv = styled.div`
  display: flex;
  flex-direction: row-reverse;
  margin: -2px 3px -2px -6px;
`;

const StyledAvatarDiv = styled.div`
  & + & {
    margin-right: -3px;
  }
`;

const StyledIconDiv = styled.div`
  margin: 2px 0 2px 1px;
`;

interface OverflowCountStyleProps {
  $isTwoDigits: boolean;
  $isThreeDigits: boolean;
}
const StyledOverflowCountDiv = styled.div<OverflowCountStyleProps>`
  display: flex;

  position: absolute;
  top: 0;
  left: 0;
  width: 100%;

  color: ${(p) => p.theme.greys.white};
  font-weight: ${(p) => (p.$isThreeDigits ? fontWeights.bold : fontWeights.semibold)};
  font-size: ${(p) => {
    if (p.$isThreeDigits) {
      return '8px';
    }
    if (p.$isTwoDigits) {
      return '8px';
    }
    return fontSizes.veryTiny;
  }};

  text-align: center;
  line-height: 24px;
`;

interface StyledPlusSignProps {
  $isSingleDigit: boolean;
}
const StyledPlusSign = styled.div<StyledPlusSignProps>`
  margin-top: -0.5px; //this element is really small so half a pixel makes a visual difference
  margin-left: ${(p) => (p.$isSingleDigit ? '6px' : '5px')};

  font-size: inherit;
`;

/*
 * Component.
 */

export const ConversationTopbarInvite: FC<ConversationTopbarInviteProps> = (props) => (
  <PopoverCoordinator
    key={props.conversation.id}
    renderModal={(modalProps) => renderDropdown(modalProps, props)}
    renderButton={(isOpen) => renderButton(isOpen, false, props)}
    keyboardAction={KeyboardActionsEnum.INVITE}
  />
);

/*
 * Button.
 */

function renderButton(isOpen: boolean, isDisabled: boolean, props: ConversationTopbarInviteProps) {
  const {conversation, trackers} = props;
  const shouldShowTrackers = computeShouldShowTrackers(conversation, trackers, {
    // Because the invite/participants button is right next to the assignee button.
    shouldHideAssignee: true,
  });

  const isConversationWithNoSubscribers = computeSubscriberCount(trackers) === 0;

  if (!shouldShowTrackers || isConversationWithNoSubscribers) {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <ToolbarButton
            label={renderTopbarInviteTooltip(props, isDisabled, shouldShowTrackers)}
            hasHover={isOpen}
            isDisabled={isDisabled}
            color={theme.greys.shade80}
            hoverColor={theme.greys.shade80}
            iconName="newContact"
            noMargin
            interactionId="viewer-invite"
          />
        )}
      </UserThemeConsumer>
    );
  }

  return (
    <Button hasHover={isOpen} isDisabled={isDisabled} interactionId="viewer-invite">
      <TooltipCoordinator render={() => renderTopbarInviteTooltip(props, isDisabled, shouldShowTrackers)}>
        <StyledInviteDiv>
          {shouldShowTrackers
            ? renderAvatars(props.trackers, props.visibleTrackers, props.otherTrackersCount, isDisabled)
            : renderIcon(props)}
          <Icon name="chevronDownSmall" />
          <VisuallyHidden>{renderLabel(props)}</VisuallyHidden>
        </StyledInviteDiv>
      </TooltipCoordinator>
    </Button>
  );
}

function renderIcon(props: ConversationTopbarInviteProps) {
  // Check if this is a team conversation.
  const {conversation} = props;
  const isTeamConversation = isTeamNamespace(conversation.namespace);

  const iconName = isTeamConversation ? 'sharedFilledSmall' : 'lockSmall';
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledIconDiv>
          <Icon name={iconName} color={theme.greys.shade80} />
        </StyledIconDiv>
      )}
    </UserThemeConsumer>
  );
}

function renderLabel(props: ConversationTopbarInviteProps) {
  // Check if this is a team conversation.
  const {conversation} = props;
  const isTeamConversation = isTeamNamespace(conversation.namespace);

  if (isTeamConversation) {
    return <Trans id="conversation-topbar-invite-label-team">Team conversation participants</Trans>;
  }

  return <Trans id="conversation-topbar-invite-label-private">Private conversation participants</Trans>;
}

export const findAvatarBorderColor = (theme: UserTheme) => {
  const buttonColors = findButtonColors(theme);
  return buttonColors[ButtonVariantsEnum.SECONDARY].background(PaletteColorsEnum.GREY);
};

const findAvatarBorderHoverColor = (theme: UserTheme) => {
  const buttonColors = findButtonColors(theme);
  return buttonColors[ButtonVariantsEnum.SECONDARY].backgroundHover(PaletteColorsEnum.GREY);
};
const findAvatarBorderDisabledColor = (theme: UserTheme) => {
  const buttonColors = findButtonColors(theme);
  return buttonColors[ButtonVariantsEnum.SECONDARY].backgroundDisabled;
};

function renderAvatars(
  trackers: ReadonlyArray<VisualConversationTrackerModel>,
  visibleTrackers: ReadonlyArray<VisualConversationTrackerModel>,
  otherTrackersCount: number,
  isDisabled: boolean,
) {
  const hasOtherAvatars = otherTrackersCount > 0;

  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledAvatarsDiv>
          {hasOtherAvatars && renderOtherTrackersCounter(otherTrackersCount, isDisabled)}
          {visibleTrackers.map((t) => (
            <StyledAvatarDiv key={t.teammate.id}>
              <TeammateAvatar
                teammate={t.teammate}
                size={24}
                borderColor={isDisabled ? findAvatarBorderDisabledColor(theme) : findAvatarBorderColor(theme)}
                borderHoverColor={findAvatarBorderHoverColor(theme)}
                isFaded={!t.hasSeenAll}
              />
            </StyledAvatarDiv>
          ))}
        </StyledAvatarsDiv>
      )}
    </UserThemeConsumer>
  );
}

function renderOtherTrackersCounter(otherTrackersCount: number, isDisabled: boolean) {
  const isThreeDigits = otherTrackersCount > 99;
  const isTwoDigits = otherTrackersCount > 9;
  const countLabel = isThreeDigits ? '+99' : otherTrackersCount;

  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledAvatarDiv>
          <Avatar
            size={24}
            borderColor={isDisabled ? findAvatarBorderDisabledColor(theme) : findAvatarBorderColor(theme)}
            borderHoverColor={findAvatarBorderHoverColor(theme)}
          >
            <AvatarBackground color={theme.greys.shade70} />
            <StyledOverflowCountDiv $isTwoDigits={isTwoDigits} $isThreeDigits={isThreeDigits}>
              <StyledPlusSign $isSingleDigit={!isTwoDigits}>{!isThreeDigits ? '+' : ''}</StyledPlusSign>
              {countLabel}
            </StyledOverflowCountDiv>
          </Avatar>
        </StyledAvatarDiv>
      )}
    </UserThemeConsumer>
  );
}

function renderTopbarInviteTooltip(
  props: ConversationTopbarInviteProps,
  isDisabled: boolean,
  shouldShowTrackers: boolean,
) {
  const {trackers, conversation} = props;

  const isTeamConversation = isTeamNamespace(conversation.namespace);

  if (isDisabled) {
    // PermissionPopoverCoordinator will provide the tooltip.
    return null;
  }

  if (!shouldShowTrackers) {
    if (isTeamConversation) {
      return <Trans id="conversation-topbar-invite-tooltip-team">Team conversation</Trans>;
    }

    return <Trans id="conversation-topbar-invite-tooltip-private">Private conversation</Trans>;
  }

  // If we have trackers, display their number.const subscribersCount = computeSubscriberCount(trackers);
  const subscribersCount = computeSubscriberCount(trackers);
  return <Trans id="conversation-topbar-invite-tooltip-subscribed">{subscribersCount} subscribed</Trans>;
}

/*
 * Dropdown.
 */

function renderDropdown(modalProps: PopoverTransferProps, props: ConversationTopbarInviteProps) {
  return (
    <InviteDropdown
      {...modalProps}
      cellsContext={props.cellsContext}
      userId={props.user.id}
      conversation={props.conversation}
      placement="bottom-end"
    />
  );
}
